import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EStorageKeys } from '../enums/storage-keys.enum';
import { ERole, IUser } from '../models/user.model';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

interface LoginResponse {
  tokens: TokenResponse;
  user: IUser;
}

interface SubscriptionResponse {
  user: IUser;
}

interface ApiResponse<T> {
  status: number;
  message: string;
  DATA: T;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private currentUser: IUser | null = null;
  public readonly authState = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router,
    private storageService: StorageService,
    private toastService: ToastService,
  ) {
    this.authState.set(this.isLoggedIn());
  }

  /**
   * Logs in a user with email and password
   * @param email User's email
   * @param password User's password
   * @returns Observable with login response
   */
  public login(email: string, password: string): Observable<ApiResponse<LoginResponse>> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.apiUrl}/auth/login`, {
        email,
        password,
      })
      .pipe(
        tap((response: any) => {
          if (response.DATA) {
            const tokens = response.DATA.tokens;
            const user = response.DATA.user;
            this.storeAuthData(tokens, user);
            this.authState.set(true);

            if (user.role === ERole.ADMIN) {
              this.router.navigate(['/admin/dashboard']);
            } else if (user.role === ERole.ARTIST) {
              this.router.navigate(['/profile/concerts']);
            } else {
              this.router.navigate(['/']);
            }
          }
        }),
        catchError((error) => {
          return throwError(() => new Error(error.error.DATA));
        }),
      );
  }

  public autoFetchCurrentUserSubscription() {
    const userId = this.getCurrentUser()?._id;
    if (userId) {
      this.getCurrentUserSubscription(userId).subscribe();
    }
  }
  public getCurrentUserSubscription(userId: string): Observable<ApiResponse<IUser>> {
    return this.http.get<ApiResponse<SubscriptionResponse>>(`${this.apiUrl}/user/${userId}`).pipe(
      tap((response: any) => {
        if (response.DATA) {
          const user = response.DATA;
          this.currentUser = user;
          this.storeUserData(user);
        }
      }),
      catchError(() => {
        return throwError(() => new Error('Beim Laden des Benutzerstatus ist ein Fehler aufgetreten'));
      }),
    );
  }

  public registerUser(userData: IUser): Observable<ApiResponse<IUser>> {
    return this.http.post<ApiResponse<IUser>>(`${this.apiUrl}/user/register/regular`, userData);
  }

  public registerArtist(userData: IUser): Observable<ApiResponse<IUser>> {
    return this.http.post<ApiResponse<IUser>>(`${this.apiUrl}/user/register/artist`, userData);
  }

  /**
   * Initiates a password reset for a user
   * @param email User's email
   * @returns Observable with password reset response
   */
  public forgotPassword(email: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/auth/forgot-password`, { email });
  }

  /**
   * Gets the current user from storage
   * @returns The current user or null if not logged in
   */

  //todo userobject should be stored in service not in LS because of manipulation
  public getCurrentUser(): IUser | null {
    const user = this.storageService.getItem(EStorageKeys.CURRENT_USER);
    return user ?? null;
  }

  public setCurrentUser(user: IUser): void {
    this.storageService.setItem(EStorageKeys.CURRENT_USER, user);
    this.currentUser = user;
  }

  /**
   * Refreshes the access token using the refresh token
   * @returns Observable with token refresh response
   */
  public refreshToken(): Observable<ApiResponse<TokenResponse>> {
    const refreshToken = this.storageService.getItem(EStorageKeys.REFRESH_TOKEN);
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<ApiResponse<TokenResponse>>(`${this.apiUrl}auth/refresh-token`, { refreshToken }).pipe(
      tap((response: ApiResponse<TokenResponse>) => {
        if (response.DATA) {
          // Store new tokens
          this.storeTokens(response.DATA);
        }
      }),
      catchError(() => {
        return throwError(() => new Error('Beim Laden des Benutzerstatus ist ein Fehler aufgetreten'));
      }),
    );
  }

  public getAccessToken(): string {
    const accessToken = this.storageService.getItem(EStorageKeys.ACCESS_TOKEN);
    return accessToken ?? '';
  }

  /**
   * Checks if the user is logged in asynchronously
   * @returns Promise that resolves to true if logged in, false otherwise
   */
  public async isLoggedInAsync(): Promise<boolean> {
    try {
      const token = await this.storageService.getItem(EStorageKeys.ACCESS_TOKEN);
      return !!token;
    } catch (error) {
      return false;
    }
  }

  public isLoggedIn(): boolean {
    try {
      const token = this.storageService.getItem(EStorageKeys.ACCESS_TOKEN);
      if (!token) {
        return false;
      }

      // Check if token is expired
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }

      const payload = JSON.parse(atob(tokenParts[1]));

      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();

      return currentTime < expirationTime;
    } catch (error) {
      return false;
    }
  }

  public logout(): void {
    this.authState.set(false);
    this.storageService.clear();
    this.currentUser = null;

    this.router.navigate(['/login']);
  }

  /**
   * Stores authentication data in storage
   * @param tokens Token response from the API
   * @param user User data
   */
  private storeAuthData(tokens: TokenResponse, user: IUser): void {
    this.storeTokens(tokens);
    this.storeUserData(user);
  }

  private storeUserData(user: IUser): void {
    this.storageService.setItem(EStorageKeys.CURRENT_USER, user);
  }

  /**
   * Stores tokens in storage
   * @param tokens Token response from the API
   */
  private storeTokens(tokens: TokenResponse): void {
    this.storageService.setItem(EStorageKeys.ACCESS_TOKEN, tokens.access_token);
    this.storageService.setItem(EStorageKeys.ID_TOKEN, tokens.id_token);
    this.storageService.setItem(EStorageKeys.REFRESH_TOKEN, tokens.refresh_token);
  }

  /**
   * Deletes the current user account
   * @returns Observable with delete response
   */
  public deleteUser(): Observable<ApiResponse<any>> {
    const user = this.getCurrentUser();

    if (!user || !user._id) {
      return throwError(() => new Error('No user logged in'));
    }

    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/user/${user._id}`).pipe(
      tap(() => {
        this.storageService.clear();
        this.currentUser = null;
      }),
      catchError((error) => {
        return throwError(() => new Error(error.error?.message || 'Failed to delete user'));
      }),
    );
  }

  /**
   * Updates the current user's profile data
   * @param userData Updated user data
   * @returns Observable with update response
   */
  public updateUser(userData: Partial<IUser>): Observable<ApiResponse<{ user: IUser }>> {
    return this.http.put<ApiResponse<{ user: IUser }>>(`${this.apiUrl}/user/${userData._id}`, userData);
  }

  /**
   * Changes the logged-in user's password (the backend takes the user from the access token)
   * @param newPassword New password
   * @returns Observable with password change response
   */
  public changePassword(newPassword: string): Observable<ApiResponse<any>> {
    return this.http
      .post<ApiResponse<any>>(`${this.apiUrl}/auth/change-password`, {
        newPassword,
      })
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(error.error?.MESSAGE || 'Failed to change password'));
        }),
      );
  }
}
